import { useState } from 'react'
import { Download, CheckSquare, Square, FileArchive, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { exportData } from '@/application/services/data-transfer.service'
import { useGetAllInvestigations } from '@/application/services/investigation.service'
import { Button } from '@/ui/components/common/Button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'

export function ExportSection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [isExporting, setIsExporting] = useState(false)
  const { data: investigations, isLoading } = useGetAllInvestigations()

  const toggleSelection = (targetId: string) => {
    const next = new Set(selectedIds)
    if (next.has(targetId)) {
      next.delete(targetId)
    } else {
      next.add(targetId)
    }
    setSelectedIds(next)
  }

  const toggleAll = () => {
    if (!investigations) return
    if (selectedIds.size === investigations.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(investigations.map((investigation) => investigation.investigationId)))
    }
  }

  const handleExport = async (exportAll: boolean = false) => {
    try {
      setIsExporting(true)
      const idsToExport = exportAll ? undefined : Array.from(selectedIds)

      if (!exportAll && idsToExport?.length === 0) {
        toast.error('Please select at least one investigation to export')
        return
      }

      const blob = await exportData(idsToExport)

      // Trigger download
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      const dateStr = new Date().toISOString().split('T')[0]
      link.download = `balivernologie-export-${dateStr}.json`
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(blobUrl)
      document.body.removeChild(link)

      toast.success('Data exported successfully')
    } catch (error: unknown) {
      const exportError = error instanceof Error ? error : new Error(String(error))
      toast.error(exportError.message || 'Failed to export data')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Export Data
            </CardTitle>
            <CardDescription className="mt-1.5">
              Download investigations, leads, and facts as a JSON file.
            </CardDescription>
          </div>
          <Button
            variant="primary"
            onClick={() => {
              void handleExport(true)
            }}
            disabled={isExporting || !investigations || investigations.length === 0}
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileArchive className="w-4 h-4 mr-2" />
            )}
            Export All Data
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col pt-0">
        <div className="flex-1 border rounded-lg overflow-hidden flex flex-col">
          <div className="bg-muted/50 p-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={toggleAll}>
              {investigations &&
              selectedIds.size === investigations.length &&
              investigations.length > 0 ? (
                <CheckSquare className="w-5 h-5 text-primary" />
              ) : (
                <Square className="w-5 h-5 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">Select All</span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void handleExport(false)
              }}
              disabled={isExporting || selectedIds.size === 0}
            >
              Export Selected ({selectedIds.size})
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto max-h-[350px] p-2 space-y-1">
            {isLoading ? (
              <div className="py-8 flex justify-center text-muted-foreground">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : !investigations || investigations.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">
                No investigations available to export.
              </div>
            ) : (
              investigations.map((investigation) => (
                <div
                  key={investigation.id}
                  className={`flex items-center gap-3 p-3 rounded-md cursor-pointer transition-colors ${
                    selectedIds.has(investigation.investigationId)
                      ? 'bg-primary/5'
                      : 'hover:bg-muted/50'
                  }`}
                  onClick={() => toggleSelection(investigation.investigationId)}
                >
                  {selectedIds.has(investigation.investigationId) ? (
                    <CheckSquare className="w-5 h-5 text-primary shrink-0" />
                  ) : (
                    <Square className="w-5 h-5 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate">{investigation.name}</h4>
                    <p className="text-xs text-muted-foreground truncate">
                      {investigation.investigationId}
                    </p>
                  </div>
                  <div className="text-xs font-medium px-2 py-1 bg-muted rounded-md shrink-0">
                    {investigation.status}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
